import * as React from "react";
import {
  Box,
  Button,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Project } from "../../types/project";
import LoadingButton from "@mui/lab/LoadingButton";
import { useUpdateProjectInternalNotesMutation } from "../../services/project.service";
import { BaseModal, StyledTextField } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalInternalNotesFormProps {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}

const ModalInternalNotesForm = ({
  open,
  onClose,
  project,
}: ModalInternalNotesFormProps) => {
  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [updateNotes, { isLoading }] = useUpdateProjectInternalNotesMutation();

  // UI Labels
  const jobInternalNotesLabel = useUILabel("project.jobInternalNotes");
  const notesLabel = useUILabel("project.notes");
  const notVisibleToIPLabel = useUILabel("project.notVisibleToIP");
  const cancelLabel = useUILabel("common.cancel");
  const submitLabel = useUILabel("common.submit");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");

  const onSubmit = async (payload: any) => {
    try {
      if (!project) return;

      await updateNotes({
        projectId: project?.id,
        internal_notes: payload.internal_notes,
      }).unwrap();
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("internal_notes", project?.internal_notes || "");
      clearErrors();
    }
  }, [project, open, setValue, clearErrors]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={isLoading}>
        {cancelLabel}
      </Button>
      <LoadingButton
        form="internal-notes-form"
        type="submit"
        variant="contained"
        loading={isLoading}
      >
        {submitLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={jobInternalNotesLabel}
      footer={footer}
      maxWidth="sm"
      useGlassCard={true}
    >
      <Box
        component="form"
        id="internal-notes-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ p: 3 }}
      >
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {notVisibleToIPLabel}
        </Typography>
        <Controller
          name="internal_notes"
          control={control}
          rules={{ required: true, minLength: 1 }}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <StyledTextField
              label={notesLabel}
              value={value || ""}
              fullWidth
              multiline={true}
              rows={8}
              onChange={onChange}
              onBlur={onBlur}
              inputRef={ref}
              error={!!errors.internal_notes}
              helperText={
                errors.internal_notes
                  ? thisFieldIsRequiredLabel
                  : undefined
              }
            />
          )}
        />
      </Box>
    </BaseModal>
  );
};
export default ModalInternalNotesForm;
